import { Switch, Route } from 'react-router'
import { useAppSelector } from '../hooks/redux'

import NetworkInterceptor from '../utilities/networkInterceptor'

import DashboardRoute from './Dashboard'
import ClientChatsRoute from './ClientChats'
import PeerChatsRoute from './PeerChats'
import CustomersRoute from './Customers'
import UsersRoute from './Users'
import TasksRoute from './Tasks'
import TimelineRoute from './Timeline'
import SettingsRoute from './Settings'
import DocumentsRoute from './Documents'
import NotificationsRoute from './Notifications'
import PushNotificationsRoute from './PushNotifications'
import ErrorRoute from './Error'
import TestRoute from './Test'
import PermissionErrorRoute from './PermissionError'
import NoPermission from '../components/molecules/NoPermission'
import Login from '../pages/Login'
import LogoutRoute from './Logout'
import NotFound from '../components/molecules/NotFound'

import AccountURLSwitch from '../pages/AccountURLSwitch'
import RestrictedRoute from './RestrictedRoute'
import PrivateRoute from './PrivateRoute'
import SuccessToast from '../components/molecules/SuccessToast'
import ErrorToast from '../components/molecules/ErrorToast'

import { selectAlert } from '../slices/alert'
import useUserPermissions from '../hooks/permissions/useUserPermissions'
import { PERMS } from '../hooks/useAccess'
import { getStoredAccount } from '../providers/AccountProvider'

export const Routes = {
  DASHBOARD: '/',
  CLIENT_CHATS: '/conversations',
  PEER_CHAT: '/peer-chat',
  CUSTOMERS: '/customers',
  USERS: '/users',
  TASKS: '/tasks',
  LOGIN: '/login',
  LOGOUT: '/logout',
  TIMELINE: '/timeline',
  SETTINGS: '/settings',
  DOCUMENTS: '/documents',
  NOTIFICATIONS: '/notifications',
  PUSH_NOTIFICATION: '/notification',
  ERROR: '/error',
  TEST: '/test',
  PERM_ERROR: '/perm-error',
  ENV_PERM_ERROR: '/env-perm-error'
}

NetworkInterceptor.setupInterceptors()

const RouteComponent = () => {
  const { success, successAutoHideDuration, error, errorAutoHideDuration } =
    useAppSelector(selectAlert)
  const { canListUsers } = useUserPermissions()

  const params = new URLSearchParams(window.location.search)

  // Handle account param
  if (params.has('_a') && getStoredAccount() !== params.get('_a')) {
    return <AccountURLSwitch onValid={() => window.location.reload()} />
  }

  return (
    <>
      <Switch>
        <PrivateRoute
          path={Routes.DASHBOARD}
          component={DashboardRoute}
          exact
        />

        <RestrictedRoute
          path={Routes.CLIENT_CHATS}
          permissions={PERMS.ConversationsBasic}
          component={ClientChatsRoute}
        />

        <RestrictedRoute
          path={Routes.PEER_CHAT}
          permissions={PERMS.PeerFull}
          component={PeerChatsRoute}
        />

        <RestrictedRoute
          path={Routes.CUSTOMERS}
          permissions={[PERMS.ContactsOwn, PERMS.ContactsAudit]}
          component={CustomersRoute}
        />

        <RestrictedRoute
          path={Routes.USERS}
          permissions={canListUsers}
          component={UsersRoute}
        />

        <RestrictedRoute
          path={Routes.TASKS}
          permissions={[PERMS.TasksOwn, PERMS.TasksAudit]}
          component={TasksRoute}
        />

        <Route path={Routes.LOGOUT} component={LogoutRoute} />

        <Route path={Routes.LOGIN} component={Login} />

        <PrivateRoute path={Routes.TIMELINE} component={TimelineRoute} />

        <PrivateRoute path={Routes.SETTINGS} component={SettingsRoute} />

        <PrivateRoute
          path={`${Routes.DOCUMENTS}/:id`}
          component={DocumentsRoute}
        />
        <PrivateRoute path={Routes.DOCUMENTS} component={DocumentsRoute} />

        <PrivateRoute
          path={Routes.NOTIFICATIONS}
          component={NotificationsRoute}
        />

        <Route
          path={Routes.PUSH_NOTIFICATION}
          component={PushNotificationsRoute}
        />

        <Route path={Routes.ERROR} component={ErrorRoute} />

        <Route path={Routes.TEST} component={TestRoute} />

        <Route path={Routes.PERM_ERROR} component={PermissionErrorRoute} />

        <Route path={Routes.ENV_PERM_ERROR} component={NoPermission} />

        <Route component={NotFound} />
      </Switch>

      {success && (
        <SuccessToast
          message={success}
          autoHideDuration={successAutoHideDuration}
        />
      )}

      {error && (
        <ErrorToast error={error} autoHideDuration={errorAutoHideDuration} />
      )}
    </>
  )
}

export default RouteComponent
